import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import FundraiserListPage from './components/FundraiserListPage';
import FundraiserDetailPage from './components/FundraiserDetailPage';
import SignUpPage from './components/SignUpPage';
import AdminPage from './components/AdminPage';
import AdminLogin from './components/AdminLogin';

// Data is now managed by the backend server

function App() {
  const [fundraisers, setFundraisers] = useState([]);
  const [pendingApplicants, setPendingApplicants] = useState([]);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';

  // Check if we're in demo mode by checking the hostname
  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname.includes('demo.') || process.env.REACT_APP_DEMO_MODE === 'true') {
      setIsDemoMode(true);
    }
  }, []);

  const fetchFundraisers = async () => {
    try {
      const response = await fetch(`${apiUrl}/fundraisers`);
      if (response.ok) {
        const data = await response.json();
        setFundraisers(data);
      }
    } catch (error) {
      console.error('Error fetching fundraisers:', error);
    }
  };

  const fetchPendingApplicants = async (authToken = null) => {
    try {
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      const response = await fetch(`${apiUrl}/pending-applicants`, { headers });
      if (response.ok) {
        const data = await response.json();
        setPendingApplicants(data);
      }
    } catch (error) {
      console.error('Error fetching pending applicants:', error);
    }
  };

  useEffect(() => {
    fetchFundraisers();
    // Don't fetch pending applicants on startup - only when admin is authenticated
    
    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      fetchFundraisers();
      // Only refresh pending applicants if we have them (means admin is logged in)
      if (pendingApplicants.length > 0) {
        fetchPendingApplicants();
      }
    }, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPendingApplicant = async (applicant) => {
    try {
      const response = await fetch(`${apiUrl}/applicants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicant),
      });
      
      if (response.ok) {
        await fetchPendingApplicants(); // Refresh the list
      }
    } catch (error) {
      console.error('Error adding applicant:', error);
    }
  };

  const approveFundraiser = async (applicantId) => {
    try {
      const response = await fetch(`${apiUrl}/approve-applicant/${applicantId}`, {
        method: 'POST',
      });
      
      if (response.ok) {
        await fetchFundraisers(); // Refresh fundraisers
        await fetchPendingApplicants(); // Refresh pending list
      }
    } catch (error) {
      console.error('Error approving applicant:', error);
    }
  };

  const updateFundraiserAmount = async (fundraiserId, newAmount) => {
    // Trigger a refresh to get updated amounts from Stripe
    await fetchFundraisers();
  };

  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gray-50">
          {/* Demo Mode Banner */}
          {isDemoMode && (
            <div className="bg-yellow-500 text-gray-900 py-2 px-4 text-center font-semibold">
              🎮 DEMO MODE - Login with: admin / password | Limited to 5 fundraisers & 2 admins
            </div>
          )}

          <nav className="bg-blue-600 text-white p-4">
            <div className="container mx-auto flex justify-between items-center">
              <h1 className="text-xl font-bold">Costa Rica Missions 2025-2026</h1>
              <div className="space-x-4">
                <Link to="/" className="hover:text-blue-200">Home</Link>
                <Link to="/signup" className="hover:text-blue-200">Join Team</Link>
                <Link to="/admin" className="hover:text-blue-200">Admin</Link>
              </div>
            </div>
          </nav>

          <Routes>
            <Route path="/" element={<FundraiserListPage fundraisers={fundraisers} />} />
            <Route path="/fundraiser/:id" element={
              <FundraiserDetailPage
                fundraisers={fundraisers}
                onDonationComplete={updateFundraiserAmount}
              />
            } />
            <Route path="/signup" element={<SignUpPage onSubmit={addPendingApplicant} />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <AdminPage
                pendingApplicants={pendingApplicants}
                onApprove={approveFundraiser}
              />
            } />
          </Routes>

          {/* Footer */}
          <footer className="bg-gray-800 text-white py-6 mt-12">
            <div className="container mx-auto px-4 text-center">
              <p className="text-sm text-gray-300">
                Brought to you by <span className="font-semibold text-white">Jaron Wilson</span>
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Costa Rica Missions 2025-2026 &copy; {new Date().getFullYear()}
              </p>
            </div>
          </footer>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;